// PageContainer.jsx
import React from 'react';
import { Container, Box } from '@mui/material';
import { CONTENT_WIDTH } from '../../theme/tokens';
import { TableWidthProvider } from './tableWidth';

// Der EINE Seitenrand (E-W5). `ViewHeader` liest ihn und die Kontext-Marke:
// ein Kopf INNERHALB eines PageContainer traegt keinen eigenen Puffer mehr
// (der Provider unten sagt es ihm), ein Kopf daneben liefert ihn selbst.
// ZWEI Kontexte, zwei Fragen: `PageContainerContext` = „verschachtelt?"
// (Boolean, unveraendert), `PageWidthContext` = „wie breit?" (das Muster).
export const PAGE_GUTTER = { xs: 1.5, sm: 3 };
export const PageContainerContext = React.createContext(false);

/**
 * PageWidthContext — das **Breitenmuster** der Seite (`'reading' | 'wide' | 'full'`).
 *
 * 🔴 Ein ZWEITER Kontext, kein umgebauter: `PageContainerContext` traegt weiter
 * den Boolean „bin ich verschachtelt", dieser hier die andere Frage „wie breit".
 * Sie zu vermischen war der Fehler, den der Grill B5 des Konzepts abgewehrt hat —
 * ein Objekt ist immer truthy, und **vier Leser** (`PageContainer`, `ViewHeader`,
 * zwei Skelette) haetten `verschachtelt = true` gelesen.
 * Quelle: `docs/forge-ux-redesign/concepts/inhaltsbreite.md` §5.4:732-777,
 * NW5.8:810.
 *
 * @useWhen
 *   - Ein Baustein traegt **denselben** Deckel wie die Flaeche, in der er steht
 *     (`ViewHeader`, Konzept §1.4:200-208 / NW1.3:230) — er liest das Muster,
 *     statt eine eigene Zahl zu fuehren
 *   - Eine Flaeche vererbt ihr Muster, ohne es weiterzureichen (ein
 *     verschachtelter `PageContainer` gibt diesen Wert **unveraendert** weiter)
 *   - 🔴 Eine Seite, deren Kopf **neben** (nicht in) einem `PageContainer` steht
 *     und die `wide`/`full` traegt: `<PageWidthContext.Provider value="…">` um
 *     Kopf **und** Container legen — sonst gilt fuer den Kopf die Vorgabe
 *     `reading` (1280) und er stuende ueber breiterem Inhalt (Konzept
 *     §1.4:206-208; sieben Geschwister-Seiten, `component-registry.md` §ViewHeader)
 *
 * @dontUseFor
 *   - „Bin ich in einem `PageContainer`?" — das ist `PageContainerContext`
 *   - Ein eigenes Mass eines Bausteins (Spalte, Rail, Lesebreite) — dafuer
 *     `CONTENT_WIDTH`/`DETAIL_RAIL_WIDTH` direkt
 *
 * @related
 *   - `PageContainerContext` — der Boolean daneben; beide werden vom selben
 *     Baustein gestellt und von `ViewHeader` zusammen gelesen
 *
 * @reuseHints
 *   - Ein Skelett einer Seite kann das Muster ihrer Seite lesen (`Skeletons/`),
 *     statt einen eigenen Deckel zu fuehren
 */
export const PageWidthContext = React.createContext('reading');

/**
 * PageContainer
 * - Forwards ref to MUI Container so transitions (Fade/Slide/Grow) work.
 * - Traegt den Seiten-Deckel (`width`) und die abgerundete "cap" ueber die
 *   innere Box; das Polster kommt aus dem `MuiContainer`-Override.
 * - Accepts extra props and optional `boxSx` to tweak the inner Box.
 * - Verschachtelt (ein `PageContainer` in einem `PageContainer`, z. B.
 *   `TaxonomyHub` > `SystemsTagsTab` > `TaxonomyGuide`) traegt er KEINEN
 *   eigenen Seitenrand — der aeussere traegt ihn bereits. Dieselbe Bauform
 *   wie `ViewHeader` (E-W5).
 *
 * The fadeInUp @keyframes is injected via MuiCssBaseline.styleOverrides in
 * theme.js, guaranteeing it's in Emotion's style context before any animation
 * rule fires. Do NOT redefine it here.
 *
 * @useWhen
 *   - Der Wurzelrahmen einer Seite (Breitenmuster + Seitenrand + Ankunftssignal)
 *   - Ein Bereich, der denselben Rand und denselben Deckel wie die Seite traegt
 *
 * @dontUseFor
 *   - Eine Karte oder ein Panel INNERHALB der Seite — dafuer `Panel`; ein
 *     zweiter `PageContainer` waere ein zweiter Seitenrahmen
 *   - Ein Kopf ohne Seite — der ist `ViewHeader`
 *
 * @related
 *   - `ViewHeader` — liest `PageWidthContext` und traegt denselben Deckel
 *   - `PAGE_GUTTER` — derselbe Rand, den der Kopf daneben selbst liefert
 *   - `CONTENT_WIDTH` (`theme/tokens.js`) — die eine Quelle der drei Deckel
 *
 * @reuseHints
 *   - Neue Seiten immer hier einhaengen; `width="wide"` statt eines eigenen
 *     `maxWidth`, `width="full"` fuer reine Arbeitsvorraete
 *
 * @props
 *   - width: 'reading' | 'wide' | 'full' (Vorgabe 'reading') — das Breitenmuster.
 *     🔴 Die Vorgabe ist der heutige Wert (1280): ohne `width` bleibt die
 *     Ausgabe unveraendert. `full` traegt **keinen** Deckel.
 *   - maxWidth: 🔴 **ENTFALLEN in G0b.5 (2026-10-13)** (Konzept §5.4:741, BW2).
 *     Der Deckel kommt allein aus `width`. Der letzte Aufrufer
 *     (`HomeDashboard.jsx`, `maxWidth="xl"` — wirkungslos) ist auf
 *     `width="wide"` umgestellt. Ein MUI-`maxWidth`-Name waere ein ZWEITER
 *     Deckel neben dem Muster und damit genau der Widerspruch, den dieser
 *     Schnitt aufloest.
 *   - sx: zusaetzliches `sx` des aeusseren Containers (gewinnt gegen die
 *     Verschachtelungsregel, weil es zuletzt gespreizt wird)
 *   - boxSx: zusaetzliches `sx` der inneren Box (der Deckel-Traeger)
 */
const PageContainer = React.forwardRef(function PageContainer(
  { children, sx, boxSx, width = 'reading', ...props },
  ref
) {
  // 🔴 E-W5, dieselbe Marke wie im `ViewHeader`: ein verschachtelter Container
  // addierte seinen Puffer auf den des aeusseren. Gemessen an
  // `/taxonomy?tab=systems` — der Inhalt sass 24 px weiter innen als der Kopf.
  const verschachtelt = React.useContext(PageContainerContext);
  const elternBreite = React.useContext(PageWidthContext);

  // 🔴 Konzept §5.4:771-777 / NW5.8:810: ein verschachtelter Container traegt
  // KEINEN Deckel (sonst addierten sich zwei Deckel wie frueher zwei Puffer)
  // und reicht das Muster **unveraendert** weiter — damit ein Kopf im Reiter
  // denselben Deckel traegt wie der Inhalt darunter. Sein eigenes `width` wirkt
  // dort nicht; der aeusserste Container entscheidet.
  const breite = verschachtelt ? elternBreite : width;
  // `full` traegt keinen Deckel (Konzept §1.1:156 / OE-W3 §7:944). Der Deckel
  // steht als SKALARER `sx`-Wert auf der Box — nicht auf MUIs `maxWidth`-Prop
  // (`<Container maxWidth={…}>` gibt ein `@media` aus, das jsdom nicht auswertet;
  // der Prop-Pfad bleibt darum ungepinnt, s. `contentWidthFull.test.jsx`).
  //
  // 🔴 G0b.5 (2026-10-13): der tote Rueckfall auf eine NICHT existierende
  // Theme-Variable (`theme.vars.layout` — es gibt kein `cssVariables: true`)
  // ist ENTFALLEN (NW5.4:806). Der Ausdruck ergab **immer** den reading-Wert.
  // Der Deckel kommt jetzt allein aus dem Token — kein Zahlen-Rueckfall daneben
  // (`inhaltsbreite.md` §5.2:687-695).
  const deckel =
    verschachtelt || breite === 'full' ? null : CONTENT_WIDTH[breite];

  return (
    <Container
      ref={ref}                 // <-- important for MUI transitions
      disableGutters
      maxWidth={false}
      sx={{ px: verschachtelt ? 0 : PAGE_GUTTER, ...sx }}
      {...props}
    >
      <Box
        // 🔴 G0b.5-Nachbesserung 2 (2026-10-13), R16-W1: die SEITENBOX braucht
        // ein benennbares Merkmal. PW14 maß bis dahin „den ersten
        // `.MuiContainer-root > .MuiBox-root` im Dokument" — auf `/overview`
        // rendert der KOPF (`ViewHeader`) aber VOR der Seite und traegt selbst
        // `Container > Box` (`ViewHeader.jsx:1060/1070`); gemessen wurde damit
        // der KOPF. Ein `data-*`-Merkmal ist additiv und macht die Flaeche
        // eindeutig (der Kopf ist kein `PageContainer`).
        data-testid="page-container"
        sx={{
          mx: 'auto',
          width: '100%',
          ...(deckel === null ? {} : { maxWidth: deckel }),
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          // ── Page build stagger ───────────────────────────────────────
          // fill-mode: backwards (not both) — the 'both' value keeps transform:translateY(0)
          // applied after the animation ends, which creates a persistent stacking context
          // on every child and causes absolutely-positioned overlays (search dropdown, tooltips)
          // to be clipped by later siblings. 'backwards' hides the element before the
          // animation starts and then lets it return to its natural CSS state (no transform).
          // 🔴 W-61: der Stagger ist WEG.
          //
          // Vorher: `fadeInUp 0.48s` plus gestaffelte Verzoegerungen bis
          // `nth-child(10): 630ms` — 1,11 s bis zur vollen Deckkraft, auf
          // JEDER Seite (55 Dateien nutzen PageContainer). Es gibt keinen
          // Routenuebergang, das WAR also die Navigationsbewegung der App.
          //
          // Und die Reihenfolge kippte: nur die Kinder 1-10 bekommen eine
          // Verzoegerung. Kind 11 hat keine und ist nach 480 ms fertig,
          // waehrend Kind 10 bis 630 ms unsichtbar bleibt — es erscheint VOR
          // seinem Vorgaenger.
          //
          // Die Frequenzregel entscheidet das: was 100+ mal am Tag gesehen
          // wird, bekommt keine Animation. Ein Werkzeug, das man den ganzen
          // Tag benutzt, soll beim Navigieren sofort da sein.
          //
          // Geblieben ist ein kurzer, GLEICHMAESSIGER Fade als Ankunftssignal —
          // 150 ms, innerhalb des Budgets fuer UI-Bewegung, ohne Verzoegerung
          // und damit ohne Reihenfolgeproblem.
          '& > *': {
            animation: 'fadeInUp 0.15s cubic-bezier(0.0, 0, 0.2, 1) backwards',
          },
          ...boxSx,
        }}
      >
        <PageWidthContext.Provider value={breite}>
          {/* 🔴 G0b.4 — der Melde-Kanal Tabelle → Kopf (`tableWidth.jsx`). Er
              rendert KEIN DOM und ändert kein Layout; ohne eine Meldung bleibt
              der Kopf bei seinem Musterdeckel (additiv). */}
          <TableWidthProvider>
            <PageContainerContext.Provider value={true}>
              {children}
            </PageContainerContext.Provider>
          </TableWidthProvider>
        </PageWidthContext.Provider>
      </Box>
    </Container>
  );
});

export default PageContainer;
